import { toast } from "react-toastify";
import {
  ApplicantType,
  AnimalScreeningReportVendors,
  VerificationNames
} from "shared/types";
import {
  Question,
  Template,
  VerificationSchema,
  VerificationSchemaNames,
  VerificationSchemas,
  VerificationSettings
} from "types/landlord/verificationTemplate";
import { validate } from "uuid";
import { NAME_MAX_LENGTH, withId } from "./additionalQuestions";
import {
  GroupRole,
  type GroupType,
  type Rule,
  type RuleSet
} from "./slices/DecisionCriteria/slices/RuleCriteria/types";

const VALID_GROUP_ROLES = new Set<GroupRole>(Object.values(GroupRole));

export function normalizeRuleGroupRoles(
  rule: Rule | { group_roles?: unknown[]; group_role?: unknown }
): GroupRole[] {
  const rawRoles = rule.group_roles;
  if (Array.isArray(rawRoles) && rawRoles.length > 0) {
    const valid = rawRoles.reduce<GroupRole[]>((acc, r) => {
      const role = (typeof r === "string" ? r : String(r)) as GroupRole;
      if (VALID_GROUP_ROLES.has(role)) acc.push(role);
      return acc;
    }, []);
    return valid.length > 0 ? valid : [GroupRole.All];
  }
  const single = rule.group_role;
  const role =
    single != null &&
    typeof single === "string" &&
    VALID_GROUP_ROLES.has(single as GroupRole)
      ? (single as GroupRole)
      : GroupRole.All;
  return [role];
}

export function resolveRuleGroupType(
  rule: Rule,
  ruleCriteria: RuleSet[] | undefined
): GroupType | undefined {
  if (rule.group_type) return rule.group_type;
  const ruleId = rule.rule_id || rule.id;
  return ruleCriteria
    ?.flatMap((ruleSet) => ruleSet.rules)
    .find((templateRule) => templateRule.id === ruleId)?.group_type;
}

export const withoutTemplateRouting = (template: Template): Template => ({
  ...template,
  routing_question: null,
  routes: [],
  variants: undefined,
  parent_template_identifier: null
});

const ADDITIONAL_QUESTIONS_VERIFICATIONS = [
  VerificationNames.RentalApplication,
  VerificationNames.HousingHistory,
  VerificationNames.CombinedIncome
] as const;

export const onSaveCleanUp = {
  petPolicy: (template: Template): Template => {
    if (!template.pet_policy) return template;

    const { restrictions: _restrictions, ...petPolicy } = template.pet_policy;

    return {
      ...template,
      pet_policy: petPolicy.enabled ? petPolicy : { enabled: false }
    };
  },
  additionalQuestions: (template: Template) => {
    // Clone template to avoid mutations
    const updatedTemplate = { ...template };

    const cleanQuestions = (questions?: Question[]) => {
      const kept = questions?.reduce<typeof questions>((acc, el) => {
        // Trim all string fields
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- sad but true
        el.name = el.name?.trim() || "";
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- sad but true
        el.title = el.title?.trim() || "";

        // If both name and title are empty, discard the entry
        if (!el.name && !el.title) return acc;

        // Ensure title and name are set correctly. A missing Name defaults to
        // the start of the question, like CRM.
        el.title ||= el.name;
        el.name ||= el.title.slice(0, NAME_MAX_LENGTH);

        const startsWithAzD = (candidate: string) =>
          /^[a-zA-Z1-9]/.test(candidate);
        // Check if el.name starts with a letter or DIGIT!

        if (!startsWithAzD(el.name))
          if (startsWithAzD(el.title))
            // if el.title starts with letter, set it to el.name
            el.name = el.title.slice(0, NAME_MAX_LENGTH);
          else {
            toast.warn(
              `Invalid question removed: "${el.name}". It must start with a letter.`
            );
            return acc;
          }

        if (RegExp(/^\d+$/).exec(el.name)) {
          toast.warn(
            `Invalid question removed: "${el.name}". It must not contain only numbers.`
          );
          return acc;
        }

        if (el.config?.widget_type === "select") {
          // clear all options are empty
          el.config.options = el.config.options?.filter((option) => option);
          // check if all options are not empty
          if (!el.config.options?.length) {
            toast.warn(
              `Invalid question removed: "${el.name}". It must have at least one option.`
            );
            return acc;
          }
        }

        acc.push(el);
        return acc;
      }, []);
      if (!kept) return kept;

      // Every question gets an id (conditional questions link to their parent
      // by it). A conditional question whose parent was just dropped above
      // goes with it: kept on its own, every applicant would be asked it.
      const withIds = kept.map(withId);
      const keptIds = new Set(withIds.map((question) => question.id));
      const orphans = withIds.filter(
        (question) =>
          question.parent_question_id &&
          !keptIds.has(question.parent_question_id)
      );
      if (orphans.length > 0)
        toast.warn(
          `Removed ${orphans.length} conditional question${
            orphans.length === 1 ? "" : "s"
          } of a removed question.`
        );
      return withIds.filter((question) => !orphans.includes(question));
    };

    const { verification_schemas } = updatedTemplate;

    Object.keys(verification_schemas).forEach((tenantType) => {
      const tenant = verification_schemas[tenantType as ApplicantType];

      ADDITIONAL_QUESTIONS_VERIFICATIONS.forEach((verificationName) => {
        const settings = tenant?.[verificationName];
        if (settings?.additional_questions)
          updatedTemplate.verification_schemas[tenantType][verificationName] = {
            ...settings,
            additional_questions: cleanQuestions(settings.additional_questions)
          };
      });
    });

    return updatedTemplate;
  },
  identityVerificationSettings: (
    template: Template,
    editedName: string
  ): Template => {
    const { settings } = template;

    const applicantTypes = Object.keys(settings) as ApplicantType[];
    const isSettingsEmpty = applicantTypes.every(
      (type) => !Object.keys(settings[type] || {}).length
    );

    if (!isSettingsEmpty) return { ...template, name: editedName };

    // Default identity verification settings
    const identityVerification = {
      identity_verification: {
        selfie_check: true,
        prevent_apply_if_failed: true
      }
    };

    return {
      ...template,
      // Inject default identity verification settings
      settings: applicantTypes.reduce(
        (acc, type) => ({
          ...acc,
          [type]: { ...settings[type], ...identityVerification }
        }),
        { ...settings } // Initialize with existing settings to preserve structure
      ),
      name: editedName
    };
  },
  onlyNewIncomePresent: (template: Template) => {
    const removeOldIncome = (verifications: VerificationSchemaNames) => ({
      ...verifications,
      [VerificationNames.IncomeVerification]: undefined,
      [VerificationNames.VerifiedIncomeVerification]: undefined
    });
    const removeAllIncomes = (
      verifications: VerificationSchemaNames
    ): Partial<VerificationSchemaNames> => ({
      ...verifications,
      [VerificationNames.IncomeVerification]: undefined,
      [VerificationNames.VerifiedIncomeVerification]: undefined,
      [VerificationNames.CombinedIncome]: undefined
    });
    const normalizeSchemas = (verifications: VerificationSchemaNames) => {
      const isNewIncomeEnabled = checkIfIncomeTypeIsEnabled(
        VerificationNames.CombinedIncome,
        verifications
      );

      if (!isNewIncomeEnabled) return removeAllIncomes(verifications);

      return removeOldIncome(verifications);
    };

    return {
      ...template,
      verification_schemas: {
        [ApplicantType.Applicant]: normalizeSchemas(
          template.verification_schemas[ApplicantType.Applicant]
        ),
        [ApplicantType.CoSigner]: normalizeSchemas(
          template.verification_schemas[ApplicantType.CoSigner]
        ),
        [ApplicantType.Occupant]: normalizeSchemas(
          template.verification_schemas[ApplicantType.Occupant] || {}
        )
      }
    };
  },
  disableBoomFinanceIfFeatureAbsent: (
    template: Template,
    hasBoomFinanceFeature: boolean
  ): Template => {
    if (hasBoomFinanceFeature) return template;

    const disableForRole = (
      verifications: VerificationSchemaNames
    ): VerificationSchemaNames => {
      const existing = verifications[VerificationNames.BoomFinanceVerification];
      if (!existing) return verifications;

      return {
        ...verifications,
        [VerificationNames.BoomFinanceVerification]: {
          ...existing,
          boom_finance_verification_enabled: false
        }
      };
    };

    return {
      ...template,
      verification_schemas: {
        [ApplicantType.Applicant]: disableForRole(
          template.verification_schemas[ApplicantType.Applicant]
        ),
        [ApplicantType.CoSigner]: disableForRole(
          template.verification_schemas[ApplicantType.CoSigner]
        ),
        [ApplicantType.Occupant]: disableForRole(
          template.verification_schemas[ApplicantType.Occupant] || {}
        )
      }
    };
  },
  // Guarantors (co-signers) can't use the purchase app. The card is hidden for
  // them in the UI, but guard the saved payload too: if it ever got enabled for
  // the co-signer role, disable it before save.
  disableBoomFinanceForGuarantors: (template: Template): Template => {
    const guarantorSchemas =
      template.verification_schemas[ApplicantType.CoSigner];
    const existing =
      guarantorSchemas?.[VerificationNames.BoomFinanceVerification];
    if (!existing?.boom_finance_verification_enabled) return template;

    return {
      ...template,
      verification_schemas: {
        ...template.verification_schemas,
        [ApplicantType.CoSigner]: {
          ...guarantorSchemas,
          [VerificationNames.BoomFinanceVerification]: {
            ...existing,
            boom_finance_verification_enabled: false
          }
        }
      }
    };
  },
  filterOutHousingHistory: (template: Template) => {
    const normalizeSchemas = (verifications: VerificationSchemaNames) => {
      if (verifications.housing_history?.address_step_enabled)
        return verifications;

      return {
        ...verifications,
        [VerificationNames.HousingHistory]: undefined
      };
    };

    return {
      ...template,
      verification_schemas: {
        [ApplicantType.Applicant]: normalizeSchemas(
          template.verification_schemas[ApplicantType.Applicant]
        ),
        [ApplicantType.CoSigner]: normalizeSchemas(
          template.verification_schemas[ApplicantType.CoSigner]
        ),
        [ApplicantType.Occupant]: normalizeSchemas(
          template.verification_schemas[ApplicantType.Occupant] || {}
        )
      }
    };
  },
  syncCreditReportMissingPricing: (
    template: Template,
    verificationSchemas: VerificationSchema[]
  ) => {
    const creditReportSchema = verificationSchemas.find(
      (schema) => schema.name === VerificationNames.CreditReport
    ) as (VerificationSchema & VerificationSettings) | undefined;

    const catalogPricing =
      creditReportSchema?.pricing ||
      creditReportSchema?.settings?.pricing ||
      [];

    if (!catalogPricing.length) return template;

    const mergePricing = (pricing?: VerificationSettings["pricing"]) => {
      const currentPricing = pricing || [];
      const mergedPricing = catalogPricing.reduce<typeof currentPricing>(
        (acc, catalogItem) => {
          const currentItem = currentPricing.find(
            (item) => item.name === catalogItem.name
          );
          acc.push(currentItem || catalogItem);
          return acc;
        },
        []
      );

      return currentPricing.reduce<typeof currentPricing>((acc, item) => {
        if (!acc.some((mergedItem) => mergedItem.name === item.name))
          acc.push(item);
        return acc;
      }, mergedPricing);
    };

    const patchRole = (role: ApplicantType) => {
      const currentRoleSchemas = template.verification_schemas[role] || {};
      const creditReport = currentRoleSchemas[VerificationNames.CreditReport];
      if (!creditReport) return currentRoleSchemas;

      return {
        ...currentRoleSchemas,
        [VerificationNames.CreditReport]: {
          ...creditReport,
          pricing: mergePricing(creditReport.pricing)
        }
      };
    };

    return {
      ...template,
      verification_schemas: {
        ...template.verification_schemas,
        [ApplicantType.Applicant]: patchRole(ApplicantType.Applicant),
        [ApplicantType.CoSigner]: patchRole(ApplicantType.CoSigner),
        [ApplicantType.Occupant]: patchRole(ApplicantType.Occupant)
      }
    };
  }
};

export const onMountTemplatePreparations = {
  additionalQuestions: (template: Template) =>
    onSaveCleanUp.additionalQuestions(template),
  // Normalizes rule_criteria: ensures each rule has group_roles (array), from API or legacy group_role.
  normalizeRuleCriteria: (
    ruleCriteria: RuleSet[] | undefined
  ): RuleSet[] | undefined => {
    if (!ruleCriteria?.length) return ruleCriteria;

    return ruleCriteria.map((ruleSet) => ({
      ...ruleSet,
      rules: ruleSet.rules.map((rule) => ({
        ...rule,
        group_roles: normalizeRuleGroupRoles(rule)
      }))
    }));
  },
  // Populate default schemas into verification_schemas FE state. Used to properly display
  // configs when a schema comes as undefined (which is expected but needs special care)
  populateDefaultSchemas: (
    defaultTemplate: Template,
    currentTemplate: Template
  ) => {
    return Object.values(ApplicantType).reduce(
      (acc, applicantType) => {
        const defaultSchemas =
          defaultTemplate.verification_schemas[applicantType] ?? {};
        const currentSchemas = {
          ...(currentTemplate.verification_schemas[applicantType] ??
            defaultSchemas)
        };

        const normalizedSchemas = [
          VerificationNames.RentalApplication,
          VerificationNames.IncomeVerification,
          VerificationNames.VerifiedIncomeVerification,
          VerificationNames.CombinedIncome
        ].reduce((acc, vName) => {
          acc[vName] = onMountTemplatePreparations.normalizeSchema(
            vName,
            currentSchemas,
            defaultSchemas
          );

          return acc;
        }, {});

        return {
          ...acc,
          [applicantType]: {
            ...currentSchemas,
            ...normalizedSchemas
          }
        };
      },
      { applicant: {}, co_signer: {}, occupant: {} }
    ) as VerificationSchemas;
  },
  normalizeSchema: (
    schemaName: VerificationNames,
    currentSchemas: VerificationSchemaNames,
    defaultSchemas: VerificationSchemaNames
  ) => {
    switch (schemaName) {
      case VerificationNames.RentalApplication: {
        const requiredFieldSteps = ["pets", "emergency_contacts", "vehicles"];
        const rentalApplicationRequired = requiredFieldSteps.reduce(
          (acc, step) => {
            const key = `${step}_step_required`;
            return {
              ...acc,
              [key]: {
                ...defaultSchemas.rental_application?.[key],
                ...currentSchemas.rental_application?.[key]
              }
            };
          },
          {}
        );

        return {
          ...currentSchemas.rental_application,
          ...rentalApplicationRequired
        };
      }
      case VerificationNames.IncomeVerification:
        return {
          ...defaultSchemas.income_verification,
          ...currentSchemas.income_verification,
          [VerificationNames.IncomeVerification + "_step_enabled"]:
            checkIfIncomeTypeIsEnabled(
              VerificationNames.IncomeVerification,
              currentSchemas
            )
        };

      case VerificationNames.CombinedIncome:
        return {
          ...defaultSchemas.combined_income_verification,
          ...currentSchemas.combined_income_verification,
          [VerificationNames.CombinedIncome + "_enabled"]:
            checkIfIncomeTypeIsEnabled(
              VerificationNames.CombinedIncome,
              currentSchemas
            )
        };

      case VerificationNames.VerifiedIncomeVerification:
      default:
        return {
          ...defaultSchemas[schemaName],
          ...currentSchemas[schemaName]
        } as VerificationSettings;
    }
  }
};

export const checkIfIncomeTypeIsEnabled = (
  schemaName: VerificationNames,
  verificationSchemas: VerificationSchemaNames
): boolean => {
  switch (schemaName) {
    case VerificationNames.IncomeVerification:
    case VerificationNames.VerifiedIncomeVerification:
      return Boolean(
        verificationSchemas.income_verification?.[
          VerificationNames.IncomeVerification + "_step_enabled"
        ]
      );
    case VerificationNames.CombinedIncome: {
      // Having an empty combined_income_verification object is an old way
      // of saying that this verification is enabled. A rare but possible
      // occasion
      const isEnabledByDeprecatedLogic =
        verificationSchemas.combined_income_verification &&
        !Object.keys(verificationSchemas.combined_income_verification).length &&
        !verificationSchemas[VerificationNames.IncomeVerification]?.[
          VerificationNames.IncomeVerification + "_step_enabled"
        ];
      const isEnabled =
        verificationSchemas[VerificationNames.CombinedIncome]?.[
          VerificationNames.CombinedIncome + "_enabled"
        ];

      return Boolean(isEnabled ?? isEnabledByDeprecatedLogic);
    }

    default:
      return false;
  }
};

export const isValidUUID = (value: string): boolean => {
  return validate(value);
};

export const isValidUrl = (value: string): boolean => {
  if (!value) return false;
  try {
    const normalized = value.startsWith("http") ? value : `https://${value}`;
    // eslint-disable-next-line compat/compat -- URL is not supported in op_mini all
    new URL(normalized);
    return true;
  } catch {
    return false;
  }
};

export const getPetScreeningSchemaSettings = (
  template: Template,
  partnerType: string,
  onConfigurationIncomplete: () => void
) => {
  const existingSchema =
    template.verification_schemas[partnerType]?.[
      VerificationNames.PetScreeningReport
    ];

  const vendor =
    existingSchema?.vendor || AnimalScreeningReportVendors.PetScreening;

  if (
    !template.verification_schemas[partnerType]?.[
      VerificationNames.PetScreeningReport
    ]?.vendor ||
    !template.verification_schemas[partnerType][
      VerificationNames.PetScreeningReport
    ].settings?.screening_url ||
    (vendor === AnimalScreeningReportVendors.OurPetPolicy &&
      !template.verification_schemas[partnerType][
        VerificationNames.PetScreeningReport
      ].settings.property_management_id)
  )
    onConfigurationIncomplete();

  return {
    ...existingSchema,
    settings: {
      ...existingSchema?.settings,
      pet_screening_enabled: true
    },
    vendor
  };
};
